import Autocomplete from '@mui/material/Autocomplete';
import Divider from '@mui/material/Divider';
import debounce from 'lodash/debounce';
import React from 'react';
import { uid } from 'react-uid';
import * as Yup from 'yup';
import { type OrganisationSummary, useListOrganisations } from '../../../../../app/ApiGen';
import { AutocompleteOption } from '../../../../../components/AutocompleteOption';
import {
  type AutocompleteContents,
  renderAutocompleteInput,
} from '../../../../../components/CustomFields/SimpleAutocomplete';
import { DataErrorHandler } from '../../../../../components/ErrorHandler';
import { WizardStep } from '../../../../../components/Wizard/WizardStep';
import { type InputComponentProps, Loading, type OnSubmit, useWizard } from '../../../../../design-system';
import { createSchema, type FormFields } from '../../../../../utils/formUtils';
import { sortByLocale } from '../../../../../utils/sortBy';
import type { JobOrganisation, JobWizardState } from '../types';

export type OrganisationDetails = Pick<JobWizardState, 'organisation'>;

const PRIORITISED_ORG_NAMES = ['Evnex Residential (Aus)', 'Evnex Residential'];
const DEFAULT_ORG_PREFIX = 'Evnex';

const EVNEX_ORGS_GROUP = 'evnex';
const OTHER_ORGS_GROUP = 'other';

const FILTER_DEBOUNCE_MS = 300;

const validationSchema = createSchema<OrganisationDetails>({
  organisation: Yup.object()
    .shape<JobOrganisation>({
      id: Yup.string().required(),
      name: Yup.string().required(),
      slug: Yup.string().required(),
    })
    .required(),
});

type OrganisationInputProps = Pick<
  InputComponentProps<JobOrganisation>,
  'disabled' | 'error' | 'helperText' | 'value' | 'name' | 'onChange'
> & { options: JobOrganisation[] };

const OrganisationInput: React.FC<OrganisationInputProps> = ({
  disabled,
  error: nameInputError,
  helperText,
  value,
  name,
  onChange,
  options,
}) => {
  const validationText = helperText ? 'Organisation is required' : undefined;
  const [filterText, setFilterText] = React.useState('');

  const applyFilter = React.useMemo(() => debounce(setFilterText, FILTER_DEBOUNCE_MS), []);
  React.useEffect(() => () => applyFilter.cancel(), [applyFilter]);

  return (
    <Autocomplete<JobOrganisation, false, true>
      disableClearable
      filterOptions={(orgs) => {
        const selectableOrgs = orgs.filter((org) => !!org.id);
        const query = filterText.trim().toLowerCase();
        if (!query) {
          return selectableOrgs;
        }
        return selectableOrgs.filter((org) => org.name.toLowerCase().includes(query));
      }}
      fullWidth
      getOptionLabel={(org) => org.name}
      groupBy={(org) => (org.name.includes(DEFAULT_ORG_PREFIX) ? EVNEX_ORGS_GROUP : OTHER_ORGS_GROUP)}
      isOptionEqualToValue={(option, org) => option.id === org.id}
      handleHomeEndKeys
      onChange={(event, selectedOrganisation) => {
        onChange(name)({ ...event, target: { ...event.target, value: selectedOrganisation } });
      }}
      onInputChange={(event, inputValue, reason) => {
        if (reason === 'input') {
          applyFilter(inputValue);
        } else {
          applyFilter.cancel();
          setFilterText('');
        }
      }}
      options={options}
      renderOption={(
        {
          'aria-disabled': ariaDisabled,
          'aria-selected': ariaSelected,
          className,
          onClick,
          onMouseMove,
          onTouchStart,
          role,
          tabIndex,
        },
        option,
        state,
      ) => (
        <AutocompleteOption
          aria-disabled={ariaDisabled}
          aria-selected={ariaSelected}
          className={className}
          index={state.index}
          key={uid(option)}
          onClick={onClick}
          onMouseMove={onMouseMove}
          onTouchStart={onTouchStart}
          role={role}
          tabIndex={tabIndex}
        >
          {option.name}
        </AutocompleteOption>
      )}
      renderGroup={(params) => (
        <li key={params.key}>
          {params.group === OTHER_ORGS_GROUP && <Divider sx={{ my: 1 }} />}
          <ul style={{ padding: 0 }}>{params.children}</ul>
        </li>
      )}
      renderInput={renderAutocompleteInput(disabled, nameInputError, validationText)}
      selectOnFocus
      value={value}
    />
  );
};

export const getOrganisationFields = (organisationOptions: JobOrganisation[]): FormFields<OrganisationDetails> => ({
  organisation: {
    id: 'organisation',
    label: 'Name',
    inputComponent: ({ disabled, error, helperText, value, name, onChange }: InputComponentProps<JobOrganisation>) => (
      <OrganisationInput
        disabled={disabled}
        error={error}
        helperText={helperText}
        name={name}
        onChange={onChange}
        options={organisationOptions}
        value={value}
      />
    ),
    valueComponent: ({ value: organisation }: AutocompleteContents<JobOrganisation>) => <>{organisation.name}</>,
  },
});

export const getSortedJobOrgs = (organisations: OrganisationSummary[]): JobOrganisation[] => {
  const jobOrganisations: JobOrganisation[] = organisations.map((org) => ({
    id: org.id,
    name: org.attributes.name,
    slug: org.attributes.slug,
  }));
  return jobOrganisations.sort(sortByLocale('name'));
};

export const getOrgsWithDefaultPrefixPrioritised = (organisations: JobOrganisation[]): JobOrganisation[] => {
  const prioritisedOrgs = PRIORITISED_ORG_NAMES.flatMap((name) => organisations.filter((org) => org.name === name));
  const otherEvnexOrgs = organisations.filter(
    (org) => !PRIORITISED_ORG_NAMES.includes(org.name) && org.name.includes(DEFAULT_ORG_PREFIX),
  );
  const otherOrgs = organisations.filter((org) => !org.name.includes(DEFAULT_ORG_PREFIX));
  return [...prioritisedOrgs, ...otherEvnexOrgs, ...otherOrgs];
};

export const OrganisationStep: React.FC = () => {
  const { state: untypedState, setState: untypedSetState, prevStep, nextStep, step } = useWizard();
  const state = untypedState as Partial<JobWizardState>;
  const setState = untypedSetState as (state: Partial<JobWizardState>) => void;

  const onCancel = React.useCallback<OnSubmit<OrganisationDetails>>(
    (values) => {
      setState({ organisation: values.organisation });
      prevStep();
    },
    [setState, prevStep],
  );

  const onSubmit = React.useCallback<OnSubmit<OrganisationDetails>>(
    (values, { setFieldError }) => {
      setFieldError('name', undefined);
      setState({ organisation: values.organisation });
      nextStep();
    },
    [setState, nextStep],
  );

  const { data, error, loading, refetch } = useListOrganisations({});

  if (loading && !data) {
    return <Loading />;
  }

  if (error || !data) {
    return (
      <DataErrorHandler description="Unable to load organisations" error={error} refetch={refetch} type="embedded" />
    );
  }

  const sortedOrganisations = getOrgsWithDefaultPrefixPrioritised(getSortedJobOrgs(data.data));

  const initialOrganisation = state.organisation ?? sortedOrganisations[0];

  const initialValues: OrganisationDetails = {
    organisation: {
      id: initialOrganisation.id,
      name: initialOrganisation.name,
      slug: initialOrganisation.slug,
    },
  };

  return (
    <WizardStep<OrganisationDetails>
      fields={Object.values(getOrganisationFields(sortedOrganisations))}
      initialValues={initialValues}
      name="Organisation"
      onCancel={onCancel}
      onSubmit={onSubmit}
      step={step}
      subtitle="Select an organisation for the job to be created in."
      validationSchema={validationSchema}
    />
  );
};
